import React, { useState, useEffect, useCallback, memo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Tabs, Tab, CircularProgress } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import { HistoryRounded as HistoryIcon } from '@material-ui/icons'
import KeyboardEventHandler from 'react-keyboard-event-handler'
import DateTime from 'luxon/src/datetime.js'
import {
  ExpandLessRounded as UpIcon,
  ExpandMoreRounded as DownIcon,
  ArrowBackRounded as BackIcon,
} from '@material-ui/icons'

import { setChatKeyword } from '../../../redux/keyword/actions'
import { isNumber, objLength } from '../../../utils/customMethods'
import messages from '../../../services/messages'
import { setAllConversations } from '../../../redux/historyConversation/actions'

import Typography from '../../atoms/display/typography'
import SearchField from '../../atoms/inputs/searchField'
import Icon from '../../atoms/display/icon'
import IconButton from '../../atoms/inputs/iconButton'
import Metric from '../../molecules/metrics/csat'
import ItemListHistory from '../../atoms/display/itemListHistory'
import HistoryConversation from './historyConversation'
import HistoryData from './historyData'
import HistoryNotes from './historyNotes'

import ProtocolSearch from '../../molecules/protocolSearch'

import useStyles from './historyStyle'
import Button from '../../atoms/inputs/button'

const History = (props) => {
  const defaultStatus = {
    loading: false,
    success: false,
    error: false,
    message: '',
  }

  const dispatch = useDispatch()
  const { t } = useTranslation()
  const classes = useStyles()
  const theme = useTheme()
  const activeConversation = useSelector((state) => state.activeConversation)
  const conversations = useSelector((state) => state.conversations)
  const conversation = conversations.conversations[activeConversation]
  const relatedComponents = conversation?.relatedComponents
  const history =
    useSelector((state) => state.history.conversations[props?.consumerId]) || {}
  const user = useSelector((state) => state.user)
  const { data: goals } = useSelector((state) => state.metrics)
  const [status, setStatus] = useState({
    get_conversations: defaultStatus,
    get_messages: defaultStatus,
    get_insights: defaultStatus,
  })
  const [currentTab, setCurrentTab] = useState(0)
  const inputSearchRef = useRef()
  const [activeItem, setActiveItem] = useState(null)
  const totalAnchorsRef = useRef(0)
  const currentAnchorRef = useRef(0)

  const attendances = Object.values(history) ?? []

  const pastAttendances = attendances.filter((item) => !item?.isResearched)
  const researchedAttendances = attendances.filter((item) => item?.isResearched)

  const attendanceActive =
    attendances.find((item) => item?.conversationId === activeItem) || {}

  const updateStatus = (action, data) => {
    if (action && data)
      setStatus((e) => ({ ...e, [action]: { ...defaultStatus, ...data } }))
  }
  const getCsat = () => {
    const reduce = (avg, value, _, { length }) => {
      return avg + parseInt(value?.csat?.replace('%', '')) / length
    }
    const chats = Object.values(history ?? {})

    return chats.reduce(reduce, 0)?.toFixed(2) + '%' ?? '-'
  }

  const getChatHistory = useCallback(async () => {
    if (!status.get_conversations.loading) {
      updateStatus('get_conversations', { loading: true })
      try {
        const response = await messages.get(
          `/tech4h-messages/consumer/${props?.consumerId}/conversations/${props?.channel}`
        )

        if (response.status === 200) {
          let message = response?.data?.message
          let chats = response?.data?.data?.reduce((chats, chat) => {
            return { ...chats, [chat.conversationId]: chat }
          }, {})
          updateStatus('get_conversations', { success: true, message: message })
          dispatch(setAllConversations(props?.consumerId, chats))
        } else {
          throw response.data
        }
      } catch (error) {
        let message =
          error?.response?.status === 500
            ? 'Unavailable service'
            : error?.response?.data?.message
        updateStatus('get_conversations', { error: true, message: message })
      }
    }
  }, [props?.chatId, activeConversation])

  const handleTabChange = useCallback((event, value) => {
    setCurrentTab(value)
    relatedComponents.history.activeTab = value
  }, [])

  const shouldUpdateList = (consumerId) => {
    if (objLength(conversations) > 0 && consumerId) {
      if (!objLength(history) > 0) {
        return true
      } else {
        const lastClosed = Object.values(conversations)
          .filter(
            (chat) =>
              chat.status === 'closed' && chat.consumer?.id === consumerId
          )
          .map((el) => el.conversationId)

        const shouldUpdade = lastClosed?.some(
          (lastClosedId) =>
            !Object.keys(history ?? {})?.some(
              (historyChatId) => historyChatId === lastClosedId
            )
        )

        return shouldUpdade
      }
    }
    return false
  }

  const renderList = useCallback(
    (list = pastAttendances) => {
      return list.map((el, key) => {
        let subTitle = ''

        const start = new Date(el.start)
        const format = `DDD - t`
        const title = DateTime.fromJSDate(start)
          .setLocale(t('translations.locale'))
          .toFormat(format)

        if (el?.isResearched) {
          subTitle = `${t('commons.Protocol')}: ${el?.protocol}`
        } else {
          subTitle = el?.users?.[el?.users?.length - 1]?.user_name
        }

        return (
          <ItemListHistory
            key={key}
            title={title}
            subtitle={subTitle}
            isResearched={el?.isResearched}
            onClick={() => {
              setActiveItem(el.conversationId)
              relatedComponents.history.activeChat.conversationId =
                el.conversationId
            }}
            active={activeItem === el.conversationId}
          />
        )
      })
    },
    [classes, activeItem, props?.consumerId, history]
  )

  const search = () => {
    if (inputSearchRef?.current?.value) {
      dispatch(setChatKeyword(inputSearchRef.current.value))
      setTimeout(() => {
        var x = document.getElementsByClassName(`anchor-history`)
        for (var i = 0; i < x.length; i++) {
          x[i].style.backgroundColor = theme.palette.highlightSearch
        }
        if (x[0]) {
          x[0].style.backgroundColor = theme.palette.highlightSearchActive
          x[0].scrollIntoView()
        }
      }, 300)
    }
  }

  const jumpToSearchAnchor = (moviment) => {
    var x = document.getElementsByClassName(`anchor-history`)
    totalAnchorsRef.current = x.length
    for (var i = 0; i < x.length; i++) {
      x[i].style.backgroundColor = theme.palette.highlightSearch
    }
    if (currentAnchorRef.current === 0 && moviment === 'up') {
      currentAnchorRef.current = x.length - 1
    } else if (
      currentAnchorRef.current === x.length - 1 &&
      moviment === 'down'
    ) {
      currentAnchorRef.current = 0
    } else {
      currentAnchorRef.current =
        moviment === 'up'
          ? currentAnchorRef.current - 1
          : currentAnchorRef.current + 1
    }
    if (x[currentAnchorRef.current]) {
      x[currentAnchorRef.current].style.backgroundColor =
        theme.palette.highlightSearchActive
      x[currentAnchorRef.current].scrollIntoView()
    }
  }

  const renderContent = () => {
    let html
    if (currentTab === 0) {
      html = (
        <>
          <div
            style={{
              backgroundColor: theme.palette.background.paper,
              boxShadow: theme.shadows[3],
              zIndex: 10,
              padding: '10px 15px',
              width: '100%',
              display: 'flex',
              flexWrap: 'nowrap',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ width: '100%' }}>
              <KeyboardEventHandler
                handleKeys={['up', 'down', 'enter']}
                onKeyEvent={(key) => {
                  if (key === 'enter') {
                    search()
                  }
                  if (key === 'up') {
                    jumpToSearchAnchor('up')
                  }
                  if (key === 'down') {
                    jumpToSearchAnchor('down')
                  }
                }}
              >
                <SearchField
                  id="field-search-history"
                  label={t('fields.search.label')}
                  placeholder={t('fields.search.placeHolder')}
                  type="text"
                  color="primary"
                  inputRef={inputSearchRef}
                  variant="outlined"
                  name="searchSkill"
                  shrink={true}
                  fullWidth={true}
                />
              </KeyboardEventHandler>
            </div>
            <div style={{ display: 'flex', flexWrap: 'nowrap' }}>
              <div style={{ width: 10 }} />
              <IconButton
                color="primary"
                onClick={() => jumpToSearchAnchor('up')}
              >
                <UpIcon style={{ color: theme.palette.primary.main }} />
              </IconButton>
              <div style={{ width: 10 }} />
              <IconButton
                color="primary"
                onClick={() => jumpToSearchAnchor('down')}
              >
                <DownIcon style={{ color: theme.palette.primary.main }} />
              </IconButton>
            </div>
          </div>
          <div className={classes.rightContent}>
            <HistoryConversation
              consumerId={props?.consumerId}
              channel={conversation?.channel}
              chatId={activeItem}
              anchor="history"
            />
          </div>
        </>
      )
    }
    if (currentTab === 1) {
      html = (
        <div className={classes.rightContent}>
          <HistoryData
            consumerId={props?.consumerId}
            chatId={activeItem}
            isHistoryData={true}
            isResearched={history?.[activeItem]?.isResearched}
            isEngagementHistory
          />
        </div>
      )
    }
    if (currentTab === 2) {
      html = (
        <div className={classes.rightContent}>
          <HistoryNotes
            consumerId={props?.consumerId}
            chatId={activeItem}
            isHistoryNotes={true}
          />
        </div>
      )
    }
    return html
  }

  useEffect(() => {
    setActiveItem(
      relatedComponents?.history?.activeChat?.conversationId ?? null
    )
  }, [activeConversation])

  useEffect(() => {
    if (shouldUpdateList(props?.consumerId)) {
      getChatHistory()
    }
  }, [props?.consumerId])

  useEffect(() => {
    setCurrentTab(relatedComponents?.history?.activeTab ?? 0)
  }, [relatedComponents])

  return (
    <>
      <div className={classes.root}>
        <div className={classes.leftHeader}>
          <Icon text={t('commons.conversationHistory')}>
            {status.get_conversations.loading ||
            user?.status?.get_user_csat?.loading ? (
              <CircularProgress size={24} />
            ) : (
              <HistoryIcon />
            )}
          </Icon>
        </div>
        {!activeItem ? (
          <>
            <div className={classes.subHeader}>
              <div>
                <ProtocolSearch
                  consumerId={props?.consumerId}
                  updateActiveItem={setActiveItem}
                />
              </div>
              <div className={classes.metricContent}>
                <Metric
                  info={t('commons.CSATInfo')}
                  value={getCsat()}
                  goal={
                    isNumber(Number(goals?.csat?.goal?.value))
                      ? Number(goals?.csat?.goal?.value) + '%'
                      : '-'
                  }
                />
              </div>
            </div>
            <div className={classes.leftContent}>
              {renderList(researchedAttendances)}
              <br />
              {!!pastAttendances?.length && (
                <>
                  <Typography
                    variant="body1"
                    style={{
                      marginLeft: 20,
                      color: theme.palette.primary.main,
                    }}
                  >
                    {pastAttendances.length}{' '}
                    {t('commons.localizedConversations')}
                  </Typography>
                  <div style={{ height: 15 }} />
                </>
              )}
              {renderList()}
            </div>
          </>
        ) : (
          <>
            <div className={classes.subHeader}>
              <Button
                className={classes.iconBack}
                onClick={() => {
                  setActiveItem(null)
                  relatedComponents.history.activeChat.conversationId = null
                }}
              >
                <BackIcon />
                <Typography>{t('buttons.back')}</Typography>
              </Button>
              <div className={classes.protocolInfo}>
                <Typography
                  variant="body1"
                  style={{
                    color: theme.palette.primary.main,
                    fontWeight: '500',
                    fontSize: 12,
                  }}
                >
                  {t('commons.Protocol')}
                </Typography>
                <Typography
                  variant="body1"
                  style={{
                    fontSize: 18,
                  }}
                >
                  {attendanceActive?.protocol ?? '-'}
                </Typography>
              </div>
            </div>
            <div className={classes.rightHeader}>
              <Tabs
                value={currentTab}
                onChange={handleTabChange}
                indicatorColor="primary"
                variant="fullWidth"
                textColor="primary"
              >
                <Tab label={t('commons.messages')} />
                <Tab label={t('commons.data')} />
                <Tab label={t('commons.notes')} />
              </Tabs>
            </div>
            {renderContent()}
          </>
        )}
      </div>
    </>
  )
}

export default memo(History)
